<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useWorkspaceTreeQuery, WorkspacesOrderBy } from "@/graphql/operations";

const props = withDefaults(
  defineProps<{
    parentId?: string;
    selected?: string;
    depth?: number;
  }>(),
  {
    parentId: undefined,
    selected: undefined,
    depth: 0,
  },
);

const emit = defineEmits<{
  change: [id: string, name: string];
  selectedLabel: [name: string];
}>();

const { data, fetching, error, executeQuery } = useWorkspaceTreeQuery({
  variables: computed(() => ({
    parentId: props.parentId,
    orderBy:
      props.parentId == null
        ? [
            WorkspacesOrderBy.TeamRankDesc,
            WorkspacesOrderBy.NameLowercaseAsc,
            WorkspacesOrderBy.IdAsc,
          ]
        : [
            WorkspacesOrderBy.TeamRankDesc,
            WorkspacesOrderBy.CreatedAtAsc,
            WorkspacesOrderBy.IdAsc,
          ],
  })),
  requestPolicy: "network-only",
});

const workspaces = computed(() => {
  const nodes = (data.value?.workspaces?.nodes ?? []).filter(
    (workspace): workspace is NonNullable<typeof workspace> =>
      workspace != null,
  );

  if (props.depth === 0) {
    return nodes.filter(
      (workspace) => workspace.myPrivileges?.mayAdministrate === true,
    );
  }

  return nodes;
});

const expandedWorkspaces = ref(new Set<string>());

function containsSelectedWorkspace(
  workspace: (typeof workspaces.value)[number],
) {
  return (
    workspace.id === props.selected ||
    workspace.descendants?.nodes?.some(
      (descendant) => descendant?.id === props.selected,
    )
  );
}

function toggle(workspaceId: string) {
  const next = new Set(expandedWorkspaces.value);

  if (next.has(workspaceId)) {
    next.delete(workspaceId);
  } else {
    next.add(workspaceId);
  }

  expandedWorkspaces.value = next;
}

function select(workspace: (typeof workspaces.value)[number]) {
  emit("change", workspace.id, workspace.name);
}

function relayChange(id: string, name: string) {
  emit("change", id, name);
}

function retry() {
  executeQuery({ requestPolicy: "network-only" });
}

watch(
  workspaces,
  (nodes) => {
    const selectedWorkspace = nodes.find(
      (workspace) => workspace.id === props.selected,
    );
    if (selectedWorkspace) {
      emit("selectedLabel", selectedWorkspace.name);
    }

    const selectedBranch = nodes.find(
      (workspace) =>
        workspace.childWorkspaces?.totalCount &&
        containsSelectedWorkspace(workspace),
    );
    if (selectedBranch) {
      expandedWorkspaces.value = new Set([
        ...expandedWorkspaces.value,
        selectedBranch.id,
      ]);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="workspace_tree" :aria-busy="fetching" :data-depth="depth">
    <div v-if="error" class="tree_error" role="alert">
      <p>{{ $t("presentationAnalyticsSelector.error") }}</p>
      <button type="button" @click="retry">
        {{ $t("presentationAnalyticsSelector.retry") }}
      </button>
    </div>

    <template v-else>
      <div
        v-for="workspace in workspaces"
        :key="workspace.id"
        class="workspace_branch"
      >
        <div
          class="workspace_item"
          :class="{ selected: selected === workspace.id }"
          :style="{ paddingLeft: `${depth * 18}px` }"
        >
          <button
            v-if="workspace.childWorkspaces?.totalCount"
            type="button"
            class="expand_button"
            :aria-expanded="expandedWorkspaces.has(workspace.id)"
            :aria-label="
              expandedWorkspaces.has(workspace.id)
                ? $t('presentationAnalyticsSelector.workspaceFilter.collapse', {
                    workspace: workspace.name,
                  })
                : $t('presentationAnalyticsSelector.workspaceFilter.expand', {
                    workspace: workspace.name,
                  })
            "
            @click="toggle(workspace.id)"
          >
            <span class="material-symbols-outlined">
              {{
                expandedWorkspaces.has(workspace.id)
                  ? "keyboard_arrow_down"
                  : "chevron_right"
              }}
            </span>
          </button>
          <span v-else class="expand_spacer" aria-hidden="true"></span>

          <button
            type="button"
            class="workspace_button"
            :aria-current="selected === workspace.id ? 'true' : undefined"
            :data-test="`presentation-analytics-workspace-${workspace.id}`"
            @click="select(workspace)"
          >
            <span
              class="material-symbols-outlined filled folder"
              aria-hidden="true"
            >
              {{
                expandedWorkspaces.has(workspace.id) ? "folder_open" : "folder"
              }}
            </span>
            <span class="workspace_name">{{ workspace.name }}</span>
          </button>
        </div>

        <PresentationAnalyticsWorkspaceTree
          v-if="expandedWorkspaces.has(workspace.id)"
          :parent-id="workspace.id"
          :selected="selected"
          :depth="depth + 1"
          @change="relayChange"
          @selected-label="emit('selectedLabel', $event)"
        />
      </div>

      <div v-if="fetching && workspaces.length === 0" class="tree_loading">
        {{ $t("presentationAnalyticsSelector.workspaceFilter.loading") }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.workspace_tree {
  min-width: 18rem;
}

.workspace_item {
  display: flex;
  align-items: center;
  min-height: 2.35rem;
  border-radius: 0.3rem;
}

.workspace_item:hover,
.workspace_item.selected {
  background: var(--classic-surface-hover, #f5f5f5);
}

.workspace_item.selected {
  box-shadow: inset 3px 0 0 var(--classic-accent, #34b5ba);
}

.expand_button,
.workspace_button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.expand_button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  padding: 0;
}

.expand_button .material-symbols-outlined {
  font-size: 1.2rem;
}

.expand_spacer {
  width: 2rem;
  min-width: 2rem;
}

.workspace_button {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 1;
  padding: 0.35rem 0.7rem 0.35rem 0;
  text-align: left;
}

.folder {
  color: var(--classic-folder, #4d93ff);
  font-size: 1.2rem;
}

.workspace_name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree_loading {
  padding: 0.8rem;
  color: var(--classic-text-muted, #736c64);
}

.tree_error {
  padding: 0.8rem;
  color: var(--classic-text-muted, #736c64);
  text-align: center;
}

.tree_error p {
  margin: 0 0 0.6rem;
}

.tree_error button {
  min-height: 2.2rem;
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--classic-border-strong, #bbb);
  border-radius: 0.3rem;
  background: var(--classic-surface, #fff);
  color: inherit;
  cursor: pointer;
}
</style>
